import { useCallback, useMemo, useState } from "react";

import classnames from "classnames";
import PropTypes from "prop-types";

import { parseListingDisplayPrice } from "lib/utils/payment/parseListingDisplayPrice";

import { useBootstrap } from "components/store/source";
import { Button, Text } from "components/ui/source";

import PaymentMethodMessaging from "services/stripe/elements/PaymentMethodMessaging";

import BookButton from "./BookButton";
import QuoteModal from "./QuoteModal";

import styles from "./Quote.module.scss";

function Quote({
  className,
  collection,
  loading,
  hasError,
  onTooltipOpen,
  onSelectButtonClick,
  onClick,
}) {
  const { available, collectionId, collectionName, longTermRental, quote } =
    collection;

  const [showQuote, setShowQuote] = useState(false);

  const isAvailable = available && quote;
  const prefix = "listing:availability.collection.quote";

  const {
    featureFlags: { bnplListingUpsellEnabled },
  } = useBootstrap();

  // Use mergedDiscount when STR or LTS booking
  const useMergedDiscount =
    !longTermRental || !quote?.invoice?.contactToBookLts;

  const handleShowQuote = useCallback(
    val => {
      setShowQuote(val);
      onTooltipOpen?.(collectionId);
    },
    [setShowQuote, onTooltipOpen, collectionId],
  );

  const stripeFormattedAmount = useMemo(() => {
    return quote?.totalLabel && bnplListingUpsellEnabled
      ? parseListingDisplayPrice(quote?.totalLabel)
      : 0;
  }, [quote?.totalLabel, bnplListingUpsellEnabled]);

  return (
    <section className={classnames(styles.container, className)}>
      {hasError && (
        <div className={styles.errorMessage}>
          <Text
            className={styles.errorTitle}
            types={["body1", "medium", "align_center"]}
            translateKey={`${prefix}.inquiryError.title`}
          />
          <Text
            types={["body1", "align_center"]}
            translateKey={`${prefix}.inquiryError.description`}
          />
        </div>
      )}
      {!hasError && isAvailable && (
        <>
          <Text
            className={styles.totalPrice}
            types={["headline4", "inline", "align_right"]}
          >
            {quote?.totalLabel}
          </Text>
          <Button
            className={styles.quoteButton}
            type="no_background"
            onClick={() => handleShowQuote(true)}
          >
            <Text
              types={["footnote", "inline"]}
              translateKey={`${prefix}.includeFees`}
            />
            <Text
              className={styles.rate}
              types={["body1", "inline", "medium", "underline"]}
            >
              {quote?.rateLabel}
              <span className={styles.infoIcon}>&#9432;</span>
            </Text>
            {quote?.originalRateLabel && (
              <Text
                className={styles.originalRate}
                types={["caption1", "inline", "medium", "strikethrough"]}
              >
                {quote?.originalRateLabel}
              </Text>
            )}
          </Button>
          <div className={styles.quoteContainer}>
            <QuoteModal
              className={styles.lineItems}
              toggleClassName={styles.showQuote}
              collectionName={collectionName}
              lineItems={quote?.invoice?.lineItems}
              loading={loading}
              useMergedDiscount={useMergedDiscount}
              showQuote={showQuote}
              onClick={onClick(collectionId)}
              onClose={event => {
                // Make onClose click event does not propagate to quote button
                event?.stopPropagation();
                handleShowQuote(false);
              }}
            />
          </div>
          {bnplListingUpsellEnabled && (
            <PaymentMethodMessaging
              className={styles.paymentMethodMessaging}
              amount={stripeFormattedAmount}
            />
          )}
          {!!quote.remainingUnitsCount && (
            <Text
              className={styles.remainingUnitsCountText}
              types={["caption2"]}
              translateKey={`checkout.ancillary.remainingUnitsCount`}
              interpolation={{ count: quote.remainingUnitsCount }}
            />
          )}
          <BookButton
            className={styles.bookButton}
            disabled={loading}
            loading={loading}
            onClick={onClick(collectionId)}
          />
          <Button
            className={classnames(styles.bookButton, styles.mobile)}
            onClick={() => {
              onSelectButtonClick?.(collectionId);
              handleShowQuote(true);
            }}
            width="fit-content"
            upperCase={false}
          >
            <Text types={["body1"]} translateKey={`${prefix}.selectButton`} />
          </Button>
        </>
      )}
      {!isAvailable && (
        <Text
          className={styles.unavailable}
          types={["body1", "medium"]}
          translateKey={`${prefix}.unavailable`}
        />
      )}
    </section>
  );
}

Quote.propTypes = {
  className: PropTypes.string,
  collection: PropTypes.shape({
    available: PropTypes.bool.isRequired,
    collectionId: PropTypes.string,
    collectionName: PropTypes.string,
    longTermRental: PropTypes.bool,
    quote: PropTypes.shape({
      invoice: PropTypes.shape({
        contactToBookLts: PropTypes.bool,
        lineItems: PropTypes.arrayOf(
          PropTypes.shape({
            key: PropTypes.string,
            label: PropTypes.string,
            value: PropTypes.string,
            primary: PropTypes.bool,
          }),
        ),
      }),
      rateLabel: PropTypes.string,
      totalLabel: PropTypes.string,
    }),
    viewCount: PropTypes.number,
  }),
  loading: PropTypes.bool,
  hasError: PropTypes.bool,
  onTooltipOpen: PropTypes.func,
  onSelectButtonClick: PropTypes.func,
  onClick: PropTypes.func.isRequired,
};

export default Quote;
